import { trpc } from "@/lib/trpc";
import { auth, googleProvider } from "@/lib/firebase";
import { signInWithPopup, onAuthStateChanged, signOut } from "firebase/auth";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { BarChart3, Sparkles, Loader2, ShieldX } from "lucide-react";

import { ROLE_LABELS } from "@shared/const";

export default function InvitePage({ params }: { params: { token: string } }) {
  const token = params.token;
  const [, setLocation] = useLocation();
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: invite, isLoading } = trpc.invites.verify.useQuery(
    { token },
    { retry: false }
  );

  const acceptMutation = trpc.invites.accept.useMutation({
    onSuccess: () => {
      setLocation("/");
      window.location.reload();
    },
    onError: err => {
      setAccepting(false);
      if (err.message === "User already registered") {
        setLocation("/");
        window.location.reload();
      } else {
        setError(err.message);
      }
    },
  });

  // When Firebase auth completes, automatically accept invite
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, firebaseUser => {
      if (firebaseUser && accepting && !acceptMutation.isPending) {
        acceptMutation.mutate({ token });
      }
    });
    return unsubscribe;
  }, [accepting, token]);

  const handleLogin = async () => {
    setError(null);
    setAccepting(true);
    try {
      // If already logged into Firebase, just accept
      if (auth.currentUser) {
        acceptMutation.mutate({ token });
        return;
      }
      await signInWithPopup(auth, googleProvider);
    } catch (e: unknown) {
      setAccepting(false);
      setError("Ошибка входа. Попробуйте ещё раз.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!invite?.valid) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <div className="flex flex-col items-center gap-6 p-10 max-w-sm w-full mx-4">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
            <ShieldX className="w-7 h-7 text-red-400" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-bold text-slate-900">
              Ссылка недействительна
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Эта ссылка приглашения уже использована или не существует.
              Попросите администратора создать новую.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const busy = accepting || acceptMutation.isPending;

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="flex flex-col items-center gap-8 p-10 max-w-sm w-full mx-4">
        {/* Logo */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-lg shadow-slate-200">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-400 border-2 border-white flex items-center justify-center">
              <Sparkles className="w-2.5 h-2.5 text-white" />
            </div>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Recruit CRM
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Система управления рекрутментом
            </p>
          </div>
        </div>

        {/* Invite card */}
        <div className="w-full bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] p-6 space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-slate-700">
              {invite.forUserName
                ? `Приглашение для ${invite.forUserName}`
                : "Вас пригласили"}
            </p>
            <p className="text-xs text-slate-400">
              Роль:{" "}
              <span className="font-medium text-slate-600">
                {ROLE_LABELS[invite.role] ?? invite.role}
              </span>
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-500 bg-red-50 rounded-lg p-2">
              {error}
            </p>
          )}

          <Button
            onClick={handleLogin}
            disabled={busy}
            variant="outline"
            className="w-full h-10 border-slate-200 hover:bg-slate-50 text-slate-700 shadow-sm"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            {busy ? "Подождите..." : "Войти через Google"}
          </Button>
        </div>

        <p className="text-[11px] text-slate-300">CHANGE Recruitment Agency</p>
      </div>
    </div>
  );
}
